import { INFINITE_PENSION_PAYOUT_INTEREST_RATE, PENSION_PAYOUT_INTEREST_RATE } from "Components/Analyses/constants";
import type { PensionGoalFormType } from "Forms/Schemas/Goals/pensionGoalFormSchema";
import { useGetAnalysis, useGetPensionGoalModeling, usePreviewPensionGoalModeling } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisDetailState, type PensionGoalModelingOverride, type PensionGoalModelingPreview } from "Infrastructure/ApiZfp/Schema";
import { DEFAULT_FEE_RATE, DEFAULT_INFLATION_RATE, DEFAULT_PENSION_DURATION } from "Infrastructure/Constants/financialConstants";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isSameDay, parseISO } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  FieldNamesMarkedBoolean,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from "react-hook-form";
import { toast } from "sonner";
import { buildPensionGoalData, buildPensionGoalModeling } from "../GoalManagement/pensionGoalPayload";
import { ageMonthsToYears, retirementAgeMonthsToDate, retirementDateToAgeMonths } from "../GoalManagement/pensionRetirementDate";

type Props = {
  analysisId: string;
  versionId: string;
  goalGuid?: string | null;
  watch: UseFormWatch<PensionGoalFormType>;
  setValue: UseFormSetValue<PensionGoalFormType>;
  getValues: UseFormGetValues<PensionGoalFormType>;
  resetField: UseFormResetField<PensionGoalFormType>;
  clearErrors: UseFormClearErrors<PensionGoalFormType>;
  trigger: UseFormTrigger<PensionGoalFormType>;
  dirtyFields: FieldNamesMarkedBoolean<PensionGoalFormType>;
  isEditMode: boolean;
  isInvalidDrawStartDate: boolean;
  clientBirthDate: Date | null;
};

// Fields whose onBlur/onChange handler triggers a waterfall recalculation. A field can fire onBlur without the
// user actually changing its value (e.g. tabbing through it) — recalculating in that case would silently overwrite
// a manually-set "Cílová částka"/"Měsíční investice" with no real cause. Track each field's last-seen value and
// only recalculate when it genuinely changed.
type ChangeGuardedField =
  | "privatePensionStartAgeMonths"
  | "privatePensionDuration"
  | "privatePensionAmount"
  | "statePensionAmount"
  | "netIncomeAmount"
  | "inflationRate"
  | "pensionInterestRate"
  | "interestRate"
  | "feeRate"
  | "targetAmount"
  | "monthlyContributionWithFee";

type RunPreviewOptions = {
  // Values the caller just committed (or is about to) — merged over the form snapshot so the request reflects
  // them even when react-hook-form hasn't propagated the change yet.
  values?: Partial<PensionGoalFormType>;
  override?: PensionGoalModelingOverride | null;
  onResult?: (preview: PensionGoalModelingPreview) => void;
  keepDerivedOnError?: boolean;
};

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getDefaultPayoutInterestRate = (infinitePension: boolean) =>
  infinitePension ? INFINITE_PENSION_PAYOUT_INTEREST_RATE : PENSION_PAYOUT_INTEREST_RATE;

export const usePensionCalculations = ({
  analysisId,
  versionId,
  goalGuid,
  watch,
  setValue,
  getValues,
  resetField,
  clearErrors,
  trigger,
  dirtyFields,
  isEditMode,
  isInvalidDrawStartDate,
  clientBirthDate,
}: Props) => {
  const { t } = useResource();

  const includeStatePension = watch("includeStatePension"); // "Počítat se starobním důchodem"
  const privatePensionStartAge = watch("privatePensionStartAge"); // "Věk odchodu do renty"
  const netIncomeAmount = watch("netIncomeAmount"); // "Čistá mzda"
  const statePensionAmount = watch("statePensionAmount"); // "Starobní důchod"
  const statePensionStart = watch("statePensionStart"); // "Starobní důchod od"
  const privatePensionAmount = watch("privatePensionAmount"); // "Měsíční renta"

  // mutateAsync, not mutate: the callbacks handed to `mutate()` are dropped if the mutation observer is torn down
  // before the response lands, which is exactly what StrictMode's double-invoked mount effect does to the initial
  // preview — the request succeeds and its result is silently discarded. A promise doesn't care.
  const { mutateAsync: previewPensionGoalModeling } = usePreviewPensionGoalModeling();

  const { data: isAnalysisInProgress } = useGetAnalysis(analysisId, versionId, {
    query: { select: (analysis) => analysis.state === AnalysisDetailState.in_progress },
  });

  // once the analysis leaves in_progress, the modeling must stay as it was while the analysis was being worked on,
  // so it's read from the frozen snapshot instead of being recomputed by the preview
  const { data: frozenModeling } = useGetPensionGoalModeling(analysisId, versionId, goalGuid ?? "", {
    query: { enabled: isAnalysisInProgress === false && !!goalGuid },
  });

  const [preview, setPreview] = useState<PensionGoalModelingPreview | null>(null);
  const derived = preview?.derived ?? null;
  const phases = preview?.phases ?? null;

  // Blur handlers can fire in quick succession, so responses may arrive out of order — only the newest one is
  // allowed to write into the form.
  const previewRequestIdRef = useRef(0);

  // Seeded once, synchronously, from each field's initial (create- or edit-mode) value — never re-seeded on
  // re-render, only ever updated by runIfFieldChanged itself as fields are genuinely edited.
  const lastSeenValuesRef = useRef<Partial<Record<ChangeGuardedField, unknown>> | null>(null);
  if (lastSeenValuesRef.current === null) {
    lastSeenValuesRef.current = {
      privatePensionStartAgeMonths: watch("privatePensionStartAgeMonths"),
      privatePensionDuration: watch("privatePensionDuration"),
      privatePensionAmount: watch("privatePensionAmount"),
      statePensionAmount: watch("statePensionAmount"),
      netIncomeAmount: watch("netIncomeAmount"),
      inflationRate: watch("inflationRate"),
      pensionInterestRate: watch("pensionInterestRate"),
      interestRate: watch("interestRate"),
      feeRate: watch("feeRate"),
      targetAmount: watch("targetAmount"),
      monthlyContributionWithFee: watch("monthlyContributionWithFee"),
    };
  }

  // Runs `handler` only if `fieldName`'s value actually changed since the last time this ran for it — a field can
  // fire onBlur without the user changing anything (e.g. tabbing through it), and that must not trigger a waterfall
  // recalculation that would silently overwrite a manually-set "Cílová částka"/"Měsíční investice".
  const runIfFieldChanged = (fieldName: ChangeGuardedField, handler: () => void) => {
    const currentValue = watch(fieldName);
    if (lastSeenValuesRef.current?.[fieldName] === currentValue) return;
    lastSeenValuesRef.current = { ...lastSeenValuesRef.current, [fieldName]: currentValue };
    handler();
  };

  // setValue/resetField wrappers for guarded fields — every programmatic write to a ChangeGuardedField must go
  // through these so lastSeenValuesRef stays in sync. A raw setValue/resetField on a guarded field leaves the ref
  // stale, so the next onBlur on that same field — even with zero user edits — sees the freshly written value as
  // "changed" and re-runs its own recalculation, drifting the value further with each blur.
  const setGuardedValue = useCallback(
    (fieldName: ChangeGuardedField, value: unknown, options?: Record<string, unknown>) => {
      setValue(fieldName, value as any, options as any);
      lastSeenValuesRef.current = { ...lastSeenValuesRef.current, [fieldName]: value };
    },
    [setValue],
  );

  const resetGuardedField = useCallback(
    (fieldName: ChangeGuardedField, options: { defaultValue?: unknown }) => {
      resetField(fieldName, options as any);
      lastSeenValuesRef.current = { ...lastSeenValuesRef.current, [fieldName]: options?.defaultValue };
    },
    [resetField],
  );

  const applyPreview = useCallback(
    (preview: PensionGoalModelingPreview, override?: PensionGoalModelingOverride | null) => {
      setPreview(preview);

      // "Hrubá mzda" is a read-only display field derived from the net salary — always mirror the server's value.
      if (preview.derived.grossSalary !== null) {
        setValue("grossIncomeAmount", preview.derived.grossSalary.toString() as any);
      }

      // A null target means an infinite pension with ~zero real appreciation, which needs unbounded capital.
      // Clear the derived fields rather than leaving a stale target/investment from before the inputs changed —
      // the schema's `required()` then blocks submission until the user adjusts the inputs.
      if (preview.targetAmount === null) {
        setGuardedValue("targetAmount", "");
        setGuardedValue("monthlyContributionWithFee", "");
        return;
      }

      // Never write back over the field the advisor just typed — the server echoes the override, but any
      // difference in rounding would visibly rewrite what they entered.
      if (override?.targetAmount == null) {
        setGuardedValue("targetAmount", preview.targetAmount.toString());
      }

      if (override?.monthlyInvestmentWithFee == null) {
        setGuardedValue("monthlyContributionWithFee", (preview.monthlyInvestmentWithFee ?? 0).toString());
      }
    },
    [setValue, setGuardedValue],
  );

  const runPreview = useCallback(
    ({ values, override, onResult, keepDerivedOnError }: RunPreviewOptions = {}) => {
      if (!isAnalysisInProgress) return;

      const formValues = { ...getValues(), ...values };
      const requestId = ++previewRequestIdRef.current;

      previewPensionGoalModeling({
        analysisId,
        versionId,
        data: {
          goalGuid: goalGuid ?? null,
          data: buildPensionGoalData(formValues),
          modeling: buildPensionGoalModeling(formValues),
          override: override ?? null,
        },
      })
        .then((preview) => {
          if (requestId !== previewRequestIdRef.current) return;
          applyPreview(preview, override);
          onResult?.(preview);
        })
        .catch(() => {
          if (requestId !== previewRequestIdRef.current) return;
          // The inputs have moved on but the target/investment haven't — clear them so the schema's `required()`
          // blocks submitting numbers that no longer match. A field the request pinned is still the advisor's input.
          if (!keepDerivedOnError) {
            if (override?.targetAmount == null) setGuardedValue("targetAmount", "");
            if (override?.monthlyInvestmentWithFee == null) setGuardedValue("monthlyContributionWithFee", "");
          }
          toast.error(t(Resources.AnalysisDetail.Toast.PensionPreviewError));
        });
    },
    [isAnalysisInProgress, analysisId, versionId, goalGuid, getValues, previewPensionGoalModeling, applyPreview, setGuardedValue, t],
  );

  useEffect(() => {
    if (frozenModeling) {
      applyPreview(frozenModeling);
    }
  }, [frozenModeling, applyPreview]);

  // Full top-down recompute: the server derives both the target amount and the monthly investment from the
  // pension inputs, so any manual pin on either of them is dropped.
  const recalculateTargetAndContribution = useCallback(
    (values?: Partial<PensionGoalFormType>, onResult?: (preview: PensionGoalModelingPreview) => void) => {
      setValue("isTargetAmountManual", false);
      setValue("isInvestmentAmountManual", false);
      runPreview({ values: { ...values, isTargetAmountManual: false, isInvestmentAmountManual: false }, onResult });
    },
    [runPreview, setValue],
  );

  // Forward-only: keep "Cílová částka" where it is and re-derive the monthly investment from it. Used by the
  // accumulation-side inputs (zhodnocení, poplatek), which by definition can't move the target.
  const recalculateMonthlyContributionFromTarget = useCallback(
    (values?: Partial<PensionGoalFormType>) => {
      const formValues = { ...getValues(), ...values };

      // Recomputing the investment overwrites it, so an investment the advisor typed earlier stops being the
      // authoritative value — the target it produced takes over as the manual one.
      const nextValues = formValues.isInvestmentAmountManual
        ? { ...values, isInvestmentAmountManual: false, isTargetAmountManual: true }
        : values;

      if (formValues.isInvestmentAmountManual) {
        setValue("isInvestmentAmountManual", false);
        setValue("isTargetAmountManual", true);
      }

      runPreview({ values: nextValues, override: { targetAmount: toNumber(formValues.targetAmount) } });
    },
    [getValues, runPreview, setValue],
  );

  const computeStatePensionStartDefault = useCallback(() => (derived ? parseISO(derived.statePensionStartDate) : null), [derived]);

  const computeStatePensionAmountDefault = useCallback(() => derived?.statePensionAmount ?? 0, [derived]);

  const computePrivatePensionAmountDefault = useCallback(() => Number(netIncomeAmount) || 0, [netIncomeAmount]);

  const clientAge = derived?.clientAge ?? 0;
  const yearsRemaining = Number(privatePensionStartAge) - clientAge;
  const isWithdrawBeforeStatePensionRequired = (phases?.monthsBeforeStatePension ?? 0) > 0;

  // The form opens with nothing computed: in create mode the server-derived defaults (state pension, its start
  // date, gross salary, client age) are what prefills the form, and in edit mode this reconciles the saved goal
  // against the server's current numbers. The whole modeling section stays hidden until this resolves, so a
  // failure here must not pass unnoticed. It waits for the analysis state, and outside in_progress the frozen
  // snapshot takes its place.
  const hasRequestedInitialPreviewRef = useRef(false);
  useEffect(() => {
    if (!isAnalysisInProgress || hasRequestedInitialPreviewRef.current) return;
    hasRequestedInitialPreviewRef.current = true;

    const values = getValues();
    const override = values.isInvestmentAmountManual
      ? { monthlyInvestmentWithFee: toNumber(values.monthlyContributionWithFee) }
      : values.isTargetAmountManual
        ? { targetAmount: toNumber(values.targetAmount) }
        : null;

    // Nothing has changed yet, so a saved target/investment still matches the saved inputs. Clearing it would block
    // saving behind validation errors in the modeling section, which stays hidden after a failed initial preview.
    runPreview({ override, keepDerivedOnError: true });
  }, [isAnalysisInProgress, getValues, runPreview]);

  useEffect(() => {
    if (!derived || dirtyFields.statePensionStart) return;

    const defaultValue = computeStatePensionStartDefault();

    // With the state pension off, "Starobní důchod od" isn't rendered, so a saved date that has already passed can't
    // be fixed by hand — it would only keep the modeling hidden. It gives way to the fresh default below.
    const isPassedHiddenDate = !includeStatePension && isInvalidDrawStartDate;

    // A goal loaded for editing may already hold a value the user customized before saving — that only shows up
    // as a mismatch against what we'd compute now, since RHF has no way to know it wasn't touched this session.
    // Mark it dirty instead of silently overwriting it with the freshly computed default.
    //
    // `setValue(name, value, { shouldDirty: true })` alone does NOT force dirty=true — RHF recomputes
    // dirty as `value !== internalDefaultValue`, and the internal default is still this same saved value, so it
    // would recompute to false. We first move the internal default to the fresh computed value via resetField
    // (which also resets the current value to it), then restore the original value with shouldDirty — now the
    // current value genuinely differs from the internal default, so RHF correctly computes dirty=true.
    if (!isPassedHiddenDate && isEditMode && statePensionStart && defaultValue && !isSameDay(statePensionStart, defaultValue)) {
      // resetField is a no-op for a field that isn't rendered, and "Starobní důchod od" only renders with the state
      // pension on. Without it the write-back below can't mark the field dirty, it only hands `watch` a new Date,
      // which re-runs this effect forever. The saved value stays as it is either way.
      if (!includeStatePension) return;

      const originalValue = statePensionStart;
      resetField("statePensionStart", { defaultValue: defaultValue as any });
      setValue("statePensionStart", originalValue, { shouldDirty: true });
      return;
    }

    if (!statePensionStart || !defaultValue || !isSameDay(statePensionStart, defaultValue)) {
      setValue("statePensionStart", defaultValue as any);
      // The server-derived default only lands after the first preview resolves — recompute now that it's known.
      recalculateTargetAndContribution({ statePensionStart: defaultValue });
    }
  }, [
    derived,
    dirtyFields.statePensionStart,
    isEditMode,
    includeStatePension,
    isInvalidDrawStartDate,
    statePensionStart,
    setValue,
    resetField,
    computeStatePensionStartDefault,
    recalculateTargetAndContribution,
  ]);

  // Same reconciliation for the other two "sticky" fields (Starobní důchod / Měsíční renta): a saved goal's
  // privatePensionAmount/statePensionAmount may already differ from what the auto-calculation would produce right
  // now, meaning the user customized them before saving. Mark them dirty so other fields' onBlur waterfall doesn't
  // silently reset them back to the default — the user can still restore fresh defaults via "Obnovit doporučené hodnoty".
  useEffect(() => {
    if (!derived || !isEditMode) return;

    const privatePensionAmountDefault = computePrivatePensionAmountDefault();
    if (!dirtyFields.privatePensionAmount && Number(privatePensionAmount) !== privatePensionAmountDefault) {
      const originalValue = privatePensionAmount;
      resetField("privatePensionAmount", { defaultValue: privatePensionAmountDefault.toString() as any });
      setGuardedValue("privatePensionAmount", originalValue, { shouldDirty: true });
    }

    if (includeStatePension) {
      const statePensionAmountDefault = computeStatePensionAmountDefault();
      if (!dirtyFields.statePensionAmount && Number(statePensionAmount) !== statePensionAmountDefault) {
        const originalValue = statePensionAmount;
        resetField("statePensionAmount", { defaultValue: statePensionAmountDefault.toString() as any });
        setGuardedValue("statePensionAmount", originalValue, { shouldDirty: true });
      }
    }
  }, [
    derived,
    isEditMode,
    includeStatePension,
    privatePensionAmount,
    statePensionAmount,
    dirtyFields.privatePensionAmount,
    dirtyFields.statePensionAmount,
    setGuardedValue,
    resetField,
    computePrivatePensionAmountDefault,
    computeStatePensionAmountDefault,
  ]);

  useEffect(() => {
    if (!statePensionStart || !isInvalidDrawStartDate) return;
    recalculateTargetAndContribution();
    clearErrors("statePensionStart");
  }, [statePensionStart, isInvalidDrawStartDate, clearErrors, recalculateTargetAndContribution]);

  // The saved age is known on mount, the client's birth date only once their query resolves.
  useEffect(() => {
    if (!clientBirthDate || getValues("privatePensionStartDate")) return;

    const ageMonths = getValues("privatePensionStartAgeMonths");
    if (ageMonths == null) return;

    setValue("privatePensionStartDate", retirementAgeMonthsToDate(clientBirthDate, ageMonths));
  }, [clientBirthDate, getValues, setValue]);

  const handleInfinitePensionClick = (newInfinitePension: boolean) => {
    // The payout rate follows the switch only while it still holds the other mode's default — a rate the advisor
    // typed, or one saved with the goal, stays.
    const isPayoutRateDefault = Number(getValues("pensionInterestRate")) === getDefaultPayoutInterestRate(!newInfinitePension);
    const nextPayoutRate = getDefaultPayoutInterestRate(newInfinitePension);
    if (isPayoutRateDefault) {
      setGuardedValue("pensionInterestRate", nextPayoutRate.toString());
    }
    const payoutRateValues = isPayoutRateDefault ? { pensionInterestRate: nextPayoutRate as any } : {};

    if (newInfinitePension) {
      setGuardedValue("privatePensionDuration", DEFAULT_PENSION_DURATION.toString());
      recalculateTargetAndContribution({
        infinitePension: newInfinitePension,
        privatePensionDuration: DEFAULT_PENSION_DURATION as any,
        ...payoutRateValues,
      });
      return;
    }

    recalculateTargetAndContribution({ infinitePension: newInfinitePension, ...payoutRateValues });
  };

  const handleIncludeStatePensionClick = (newIncludeStatePension: boolean) => {
    let nextStatePensionAmount = statePensionAmount || 0;
    let nextStatePensionStart = statePensionStart;

    if (newIncludeStatePension && derived) {
      nextStatePensionAmount = computeStatePensionAmountDefault();
      nextStatePensionStart = computeStatePensionStartDefault();
      // These fields only render once the switch is on, and resetField does nothing for a field that isn't
      // rendered yet. Writing the values first keeps the form in line with the recalculation below.
      setGuardedValue("statePensionAmount", nextStatePensionAmount.toString());
      setValue("statePensionStart", nextStatePensionStart as any);
      resetGuardedField("statePensionAmount", { defaultValue: nextStatePensionAmount.toString() });
      resetField("statePensionStart", { defaultValue: nextStatePensionStart as any });
    }

    recalculateTargetAndContribution({
      includeStatePension: newIncludeStatePension,
      statePensionAmount: nextStatePensionAmount,
      statePensionStart: nextStatePensionStart,
    });
  };

  // "Věk odchodu do renty" and "Datum odchodu do renty" are two views of privatePensionStartAgeMonths, which is what
  // gets sent. The masked age input reports programmatic writes as changes too, so an age that still matches the
  // stored months must leave them alone — otherwise picking 11/2058 would snap straight back to the birth month.
  const handleRetirementAgeChange = (value: string) => {
    const age = Number(value);
    if (value === "" || !Number.isInteger(age) || age <= 0) {
      setValue("privatePensionStartAgeMonths", undefined);
      return;
    }

    const currentAgeMonths = getValues("privatePensionStartAgeMonths");
    if (currentAgeMonths != null && ageMonthsToYears(currentAgeMonths) === age) return;

    const ageMonths = age * 12;
    setValue("privatePensionStartAgeMonths", ageMonths);
    if (clientBirthDate) {
      setValue("privatePensionStartDate", retirementAgeMonthsToDate(clientBirthDate, ageMonths));
    }
  };

  const handleRetirementDateChange = (date: Date | null) => {
    if (!date || !clientBirthDate) return;

    const ageMonths = retirementDateToAgeMonths(clientBirthDate, date);
    if (ageMonths <= 0) return;

    setValue("privatePensionStartAgeMonths", ageMonths);
    setValue("privatePensionStartAge", ageMonthsToYears(ageMonths).toString() as any, { shouldValidate: true });
  };

  const handleRetirementAgeBlur = () => {
    // The date is rewritten silently while the age is being typed, so its validation catches up here.
    if (clientBirthDate && getValues("privatePensionStartAgeMonths") != null) {
      trigger("privatePensionStartDate");
    }
    runIfFieldChanged("privatePensionStartAgeMonths", () => recalculateTargetAndContribution());
  };

  const handleRetirementDateBlur = () => runIfFieldChanged("privatePensionStartAgeMonths", () => recalculateTargetAndContribution());

  const handlePrivatePensionDurationBlur = () => runIfFieldChanged("privatePensionDuration", () => recalculateTargetAndContribution());
  const handlePrivatePensionAmountBlur = () => runIfFieldChanged("privatePensionAmount", () => recalculateTargetAndContribution());
  const handleStatePensionAmountBlur = () => runIfFieldChanged("statePensionAmount", () => recalculateTargetAndContribution());
  const handleInflationBlur = () => runIfFieldChanged("inflationRate", () => recalculateTargetAndContribution());
  const handleWithdrawInterestRateBlur = () => runIfFieldChanged("pensionInterestRate", () => recalculateTargetAndContribution());

  const handleNetIncomeAmountBlur = () =>
    runIfFieldChanged("netIncomeAmount", () => {
      const privatePensionAmountDefault = Number(netIncomeAmount) || 0;
      const shouldResetPrivatePension = !dirtyFields.privatePensionAmount;

      if (shouldResetPrivatePension) {
        resetGuardedField("privatePensionAmount", { defaultValue: privatePensionAmountDefault.toString() });
      }

      recalculateTargetAndContribution(
        shouldResetPrivatePension ? { privatePensionAmount: privatePensionAmountDefault } : undefined,
        (preview) => {
          // "Starobní důchod" is derived from the salary on the server, so its new default only arrives with the
          // response — adopt it and recompute around it, unless the advisor has customized the field.
          const statePensionAmountDefault = preview.derived.statePensionAmount;
          if (statePensionAmountDefault === null) return;
          if (!watch("includeStatePension") || dirtyFields.statePensionAmount) return;
          if (Number(watch("statePensionAmount")) === statePensionAmountDefault) return;

          resetGuardedField("statePensionAmount", { defaultValue: statePensionAmountDefault.toString() });
          recalculateTargetAndContribution({ statePensionAmount: statePensionAmountDefault });
        },
      );
    });

  const handleIncludeInflationClick = (checked: boolean) => {
    const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;
    const values = { includeInflation: checked, inflationRate: newInflationRate as any };

    if (Number(watch("privatePensionDuration")) > 0 && Number(privatePensionStartAge) > 0 && statePensionStart) {
      recalculateTargetAndContribution(values);
    } else {
      recalculateMonthlyContributionFromTarget(values);
    }

    setGuardedValue("inflationRate", newInflationRate.toString());
  };

  const handleIncludeFeeChange = (checked: boolean) => {
    const newFeeRate = checked ? DEFAULT_FEE_RATE : 0;
    setGuardedValue("feeRate", newFeeRate.toString());
    recalculateMonthlyContributionFromTarget({ includeFee: checked, feeRate: newFeeRate as any });
  };

  const handleFeeRateBlur = () => runIfFieldChanged("feeRate", () => recalculateMonthlyContributionFromTarget());
  const handleInterestRateBlur = () => runIfFieldChanged("interestRate", () => recalculateMonthlyContributionFromTarget());

  // "Cílová částka" and "Měsíční investice" are read-only for now, so their edit handlers are commented out until the
  // new behaviour settles.
  // const handleTargetAmountBlur = () =>
  //   runIfFieldChanged("targetAmount", () => {
  //     setValue("isTargetAmountManual", true);
  //     setValue("isInvestmentAmountManual", false);
  //     runPreview({
  //       values: { isTargetAmountManual: true, isInvestmentAmountManual: false },
  //       override: { targetAmount: toNumber(watch("targetAmount")) },
  //     });
  //   });

  // const handleMonthlyContributionWithFeeBlur = (newMonthlyContributionWithFee: number) =>
  //   runIfFieldChanged("monthlyContributionWithFee", () => {
  //     setValue("isInvestmentAmountManual", true);
  //     setValue("isTargetAmountManual", false);
  //     runPreview({
  //       values: { isInvestmentAmountManual: true, isTargetAmountManual: false },
  //       override: { monthlyInvestmentWithFee: toNumber(newMonthlyContributionWithFee) },
  //     });
  //   });

  const handleResetToDefaults = () => {
    const privatePensionAmountDefault = computePrivatePensionAmountDefault();
    resetGuardedField("privatePensionAmount", { defaultValue: privatePensionAmountDefault.toString() });

    let statePensionAmountDefault = 0;
    let statePensionStartDefault: Date | null = statePensionStart ?? null;

    if (includeStatePension) {
      statePensionAmountDefault = computeStatePensionAmountDefault();
      statePensionStartDefault = computeStatePensionStartDefault();
      resetGuardedField("statePensionAmount", { defaultValue: statePensionAmountDefault.toString() });
      resetField("statePensionStart", { defaultValue: statePensionStartDefault as any });
    }

    recalculateTargetAndContribution({
      privatePensionAmount: privatePensionAmountDefault,
      statePensionAmount: statePensionAmountDefault,
      statePensionStart: statePensionStartDefault,
    });
  };

  return {
    preview,
    calculations: {
      clientAge,
      yearsRemaining,
      isWithdrawBeforeStatePensionRequired,
    },
    handlers: {
      handleInfinitePensionClick,
      handleIncludeStatePensionClick,
      handleRetirementAgeChange,
      handleRetirementAgeBlur,
      handleRetirementDateChange,
      handleRetirementDateBlur,
      handlePrivatePensionDurationBlur,
      handleNetIncomeAmountBlur,
      handlePrivatePensionAmountBlur,
      handleStatePensionAmountBlur,
      handleInflationBlur,
      handleWithdrawInterestRateBlur,
      handleIncludeInflationClick,
      handleIncludeFeeChange,
      handleFeeRateBlur,
      handleInterestRateBlur,
      // handleTargetAmountBlur,
      // handleMonthlyContributionWithFeeBlur,
      handleResetToDefaults,
    },
    defaults: {
      computeStatePensionStartDefault,
      computeStatePensionAmountDefault,
      computePrivatePensionAmountDefault,
    },
  };
};
